import * as React from 'react';
import { PROJECT_STATUS, QUICKTAG_STATUS } from '@nexxt/common/types';
import { Tooltip } from '@mui/material';
import { UilCommentAltChartLines, UilChannel } from '@iconscout/react-unicons';

interface DirectoryCardProps {
    cardType?: 'full' | 'simple';
    cardLink: () => void;
    status: string;
    name: string;
    lastUpdated: string;
    lastUpdatedBy?: string;
    lastUpdatedByImage?: string;
    children?: JSX.Element;
    clientTerm?: string;
    isDisabled?: boolean;
    showWatermark?: boolean;

    // Platform
    clientImage?: string;
    clientName?: any; // TODO: fix inconsistency with client typing

    // Quicktag
    isQuicktag?: boolean;
    verbatimCount?: number;
}

interface CardSectionProps {
    header?: string;
    content?: string | number;
    image?: string;
    tooltipText?: string;
    hideImage?: boolean;
}

function CardSection({
    header,
    content,
    image,
    hideImage,
    tooltipText
}: CardSectionProps) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="uppercase text-gray-500 text-xs">{header}</span>
            <Tooltip title={tooltipText}>
                <div className="flex flex-row items-center">
                    {!hideImage && image && (
                        <img
                            className="w-4 h-4 rounded-full mr-1"
                            src={image}
                        />
                    )}
                    <span>{content}</span>
                </div>
            </Tooltip>
        </div>
    );
}

export default function DirectoryCard({
    cardType = 'full',
    isQuicktag = false,
    cardLink,
    status,
    name,
    clientImage,
    clientName,
    lastUpdated,
    lastUpdatedBy,
    lastUpdatedByImage,
    verbatimCount,
    children,
    clientTerm = 'Client',
    isDisabled,
    showWatermark = false
}: DirectoryCardProps) {
    const fullCard = cardType !== 'simple';

    const statusVariants = {
        [PROJECT_STATUS.LIVE]: 'bg-green text-white',
        [PROJECT_STATUS.DRAFT]: 'bg-amber-300 text-black',
        [PROJECT_STATUS.PAUSED]: 'bg-amber-300 text-black',
        [PROJECT_STATUS.COMPLETE]: 'bg-neutral-700 text-white',
        [PROJECT_STATUS.ARCHIVED]: 'bg-neutral-700 text-white',
        [PROJECT_STATUS.REVIEW]: 'bg-neutral-700 text-white',
        [QUICKTAG_STATUS.ANALYZING]: 'bg-amber-300 text-black',
        [QUICKTAG_STATUS.READY]: 'bg-green text-white'
    };

    return (
        <div
            className={`relative w-full bg-white rounded-lg flex flex-col hover:drop-shadow-md hover:z-10`}
        >
            {showWatermark && (
                <div className="absolute bottom-4 right-4 text-gray-200/60">
                    {isQuicktag ? (
                        <UilChannel size={80} />
                    ) : (
                        <UilCommentAltChartLines size={80} />
                    )}
                </div>
            )}
            <div
                className={`relative px-3.5 py-3 flex flex-col ${
                    fullCard ? 'h-44' : 'h-32'
                } ${isDisabled ? 'cursor-default' : 'cursor-pointer'}`}
                onClick={!isDisabled && cardLink}
            >
                <div className="flex flex-col">
                    <div className="mb-1">
                        {status && (
                            <span
                                id={`project-status-${status}`}
                                className={`px-2 rounded-full text-xs uppercase font-semibold leading-none ${statusVariants[status]}`}
                            >
                                {status}
                            </span>
                        )}
                    </div>
                    <div className="flex flex-col">
                        <Tooltip title={name.length > 40 ? name : null}>
                            <p className="font-semibold text-xl overflow-hidden line-clamp-2 mb-0">
                                {name}
                            </p>
                        </Tooltip>
                        {(!fullCard || isQuicktag) && (
                            <span className="text-sm text-gray-500 mt-0.5">
                                {clientName}
                            </span>
                        )}
                    </div>
                </div>
                {fullCard && (
                    <div className="flex flex-grow flex-col justify-end mt-1">
                        <div className="grid grid-cols-2">
                            {isQuicktag ? (
                                verbatimCount ? (
                                    <CardSection
                                        header={'# of Verbatims'}
                                        content={verbatimCount}
                                    />
                                ) : null
                            ) : (
                                <CardSection
                                    header={clientTerm}
                                    content={clientName}
                                    image={clientImage}
                                    hideImage={isQuicktag}
                                />
                            )}
                            <CardSection
                                header="Last Updated"
                                content={lastUpdated}
                                tooltipText={
                                    lastUpdatedBy
                                        ? `Last updated by ${lastUpdatedBy}`
                                        : null
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="relative px-3.5">
                <div
                    className={`flex flex-row flex-nowrap items-center gap-2 h-12 ${
                        !showWatermark ? 'border-t' : ''
                    }`}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
